import { createContext, useContext, useState } from 'react';
import {
  platformChannel, tenantChannel,
  type LiveCallsData, type LiveExtension, type LiveQueue, type LiveQueuesData,
} from '@arcnet/contracts';
import { useSession } from '../../lib/auth/session.js';
import { useChannels, type RealtimeState } from '../../lib/api/realtime.js';

/**
 * The switch's live state, shared by every realtime page.
 *
 * Calls and queues are separate channels on the tab's one WebSocket, so a page
 * asks for the topics it shows and hears nothing else: the extensions page is
 * not re-rendered by a queue filling up. Each message is the channel's whole
 * current view, a snapshot on subscribing and an update on every change, so
 * there is nothing to fetch first and nothing to poll after.
 *
 * Scope is resolved here too. A platform operator watches the platform
 * channels; a tenant user watches their own. The API refuses a platform channel
 * without platform scope, so getting this wrong is a refusal rather than a
 * leak, but it is still the page's job to ask for the right thing.
 */

export type LiveTopic = 'calls' | 'queues';

export interface LiveState {
  extensions: LiveExtension[];
  queues: LiveQueue[];
  connection: RealtimeState;
  /**
   * How many nodes reported recently enough to be believed.
   *
   * Zero means nothing on the switch side is talking, and every number on the
   * page is a leftover -- which is worth saying out loud on a page whose whole
   * claim is that it is live. Undefined until the first message lands, so a
   * page can tell "no nodes" apart from "have not heard yet".
   */
  nodes: number | undefined;
  /**
   * True once every requested topic has delivered.
   *
   * Distinguishes "nothing is happening" from "we have not heard yet", which
   * look identical without it and mean opposite things on a live page.
   */
  streamed: boolean;
  /**
   * The server's clock minus this browser's, in ms, from the last message.
   *
   * `since` and `longest_waiting_since` are the switch's epoch ms. A browser
   * whose clock is two minutes out would otherwise show every caller as having
   * waited two minutes, or not at all.
   */
  skewMs: number;
}

/**
 * The account a live page is about, when it is shown inside an account's console (30 September
 * 2026). Platform and MSP staff open an account from the list and watch that one; its own people
 * watch theirs. Outside a console there is none, and the page falls back to the signed-in scope.
 */
const LiveAccount = createContext<string | null>(null);
export const LiveAccountProvider = LiveAccount.Provider;

/** The account to show: the console's, else the signed-in one. */
export function useLiveTenant(): string | null {
  const opened = useContext(LiveAccount);
  const active = useSession((s) => s.activeTenantId);
  return opened ?? active;
}

export function useLiveScope(): { platform: boolean; tenantId: string | null } {
  const opened = useContext(LiveAccount);
  const session = useSession((s) => s.session);
  const activeTenantId = useSession((s) => s.activeTenantId);
  if (opened) return { platform: false, tenantId: opened };
  const platform = session?.scope.all_tenants === true;
  return { platform, tenantId: platform ? null : activeTenantId };
}

interface Received { data: LiveCallsData | LiveQueuesData; receivedAt: number; skewMs: number }

export function useLiveState(topics: readonly LiveTopic[] = ['calls', 'queues']): LiveState {
  const { platform, tenantId } = useLiveScope();
  const channelFor = (topic: LiveTopic): string | null =>
    platform ? platformChannel(topic) : tenantId ? tenantChannel(tenantId, topic) : null;
  const callsChannel = topics.includes('calls') ? channelFor('calls') : null;
  const queuesChannel = topics.includes('queues') ? channelFor('queues') : null;
  const channels = [callsChannel, queuesChannel].filter((c): c is string => c !== null);

  /**
   * Keyed by channel name, so switching account never renders the last
   * account's calls under the new one's heading: its entry is simply not read.
   */
  const [received, setReceived] = useState<Record<string, Received>>({});

  const connection = useChannels(channels, (msg) => {
    if (msg.type !== 'snapshot' && msg.type !== 'update') return;
    const now = Date.now();
    const entry: Received = { data: msg.data, receivedAt: now, skewMs: Date.parse(msg.at) - now };
    setReceived((prev) => {
      const next: Record<string, Received> = { [msg.channel]: entry };
      // Only the channels still wanted survive, so the map does not grow with
      // every account an operator has looked at.
      for (const c of channels) if (c !== msg.channel && prev[c]) next[c] = prev[c];
      return next;
    });
  });

  const calls = callsChannel ? received[callsChannel] : undefined;
  const queues = queuesChannel ? received[queuesChannel] : undefined;
  const latest = [calls, queues]
    .filter((r): r is Received => r !== undefined)
    .sort((a, b) => b.receivedAt - a.receivedAt)[0];

  return {
    extensions: calls && 'extensions' in calls.data ? calls.data.extensions : [],
    queues: queues && 'queues' in queues.data ? queues.data.queues : [],
    connection,
    nodes: latest?.data.nodes,
    streamed: channels.length > 0 && channels.every((c) => received[c] !== undefined),
    skewMs: latest?.skewMs ?? 0,
  };
}
